import { TableStateWithFilters } from "@king-ict/eupisi-ui/components";
import {
  CodeBook,
  ImportantDate,
  PagedResult,
  ExportFiltersData,
} from "@king-ict/eupisi-ui/models";
import { api } from "@king-ict/eupisi-ui/utils";

import { AdministrationListFiltersData } from "../components";
import { StatusHistoryTableData } from "../components/StatusHistoryTable";
import {
  Criterion,
  InstitutionCriterion,
  PedagogicalYear,
  RequestFilters,
  RequestSearchItem,
  RequestPersonInfo,
  RequestBasicInfo,
  RequestCriterionData,
  LocationProgramListItem,
  Location,
  LocationProgramData,
  RequestCriterion,
  UnlockReason,
  InitialConversationQuestionGroup,
  LocationProgramDataListItem,
  LocationProgramDataListFilters,
  User,
  CheckDataResult,
  RequestAuthorization,
  SubmitAuthorization,
  CancelAuthorization,
  CanCreateRequestResult,
  UserAuthData,
  InstitutionListItem,
  EnrollmentFilters,
  EnrollmentSearchItem,
  ConfirmRejectEnrollment,
  Enrollment,
  InstitutionFounderEnrollmentGroup,
  RequestComment,
  CreateUpdateRequestComment,
  RequestPhysicalExamination,
  InstitutionBenefit,
  Benefit,
  PedagogicalYearImportantDate,
  MonthImportantDate,
  LocationProgramExportFilterData,
  InstitutionDetailsData,
  Beneficiary,
  BeneficiaryFilter,
  ErrorUserData,
  PhysicalExaminationData
} from "../models";
import { UnlockRequest } from "../models/Request/UnlockRequest";

const pedagogicalYearsUrlPrefix: string = "pedagogical-years";
const importantDatesUrlPrefix: string = "important-dates";
const criteriaUrlPrefix: string = "criteria";
const requestStatusesUrlPrefix: string = "request-statuses";
const requestStatusesHistoryUrlPrefix: string = "request-statuses-history";
const institutionsUrlPrefix: string = "institutions";
const locationsUrlPrefix: string = "locations";
const enrollmentGroupUrlPrefix: string = "enrollment-groups";
const requestsUrlPrefix: string = "requests";
const authorizationPrefix: string = "e-authorization";
const documentsUrlPrefix: string = "documents";
const personsUrlPrefix: string = "persons";
const workOrganizationsUrlPrefix: string = "work-organizations";
const userUrlPrefix = "users";
const enrollmentListsUrlPrefix: string = "enrollment-lists";
const enrollmentStatusesUrlPrefix: string = "enrollment-statuses";
const enrollmentRejectReasonUrlPrefix: string = "enrollment-reject-reason";
const monthsUrlPrefix: string = "months";
const benefitsUrlPrefix: string = "benefits";
const beneficiaryUrlPrefix: string = "beneficiary";
const mailUrlPrefix: string = "mail";
const exchangeRateUrlPrefix: string = "exchangeRate";

export const getPedagogicalYears = async () => {
  return api.get(`${pedagogicalYearsUrlPrefix}`) as Promise<PedagogicalYear[]>;
};

export const getMonths = async () => {
  return api.get(`${monthsUrlPrefix}`) as Promise<CodeBook[]>;
};

export const getCurrentPedagogicalYear = async () => {
  return api.get(
    `${pedagogicalYearsUrlPrefix}/current`
  ) as Promise<PedagogicalYear>;
};

export const getCurrentImportantDate = async () => {
  return api.get(
    `${importantDatesUrlPrefix}/current`
  ) as Promise<ImportantDate>;
};

export const getCriteria = async () => {
  return api.get(`${criteriaUrlPrefix}`) as Promise<Criterion[]>;
};

export const searchInstitutionCriteria = async (
  filters: AdministrationListFiltersData
) => {
  return api.get(
    `${institutionsUrlPrefix}/${criteriaUrlPrefix}/search`,
    filters
  ) as Promise<InstitutionCriterion[]>;
};

export const getInstitutionCriterion = async (id: number) => {
  return api.get(
    `${institutionsUrlPrefix}/${criteriaUrlPrefix}/${id}`
  ) as Promise<InstitutionCriterion>;
};

export const getInstitutionInitialConversationEnabled = async () => {
  return api.get(
    `${institutionsUrlPrefix}/initial-conversation`
  ) as Promise<boolean>;
};

export const putInstitutionInitialConversationEnabled = async (
  input: boolean
) => {
  return api.put(
    `${institutionsUrlPrefix}/initial-conversation`,
    { isEnabled: input }
  ) as Promise<any>;
};

export const getConsent = async () => {
  return api.download(`${documentsUrlPrefix}/consent`) as Promise<any>;
};

export const getInitialConversation = async () => {
  return api.download(`${documentsUrlPrefix}/initial-conversation`) as Promise<any>;
};

export const getTermsOfService = async () => {
  return api.download(`${documentsUrlPrefix}/terms-of-service`) as Promise<any>;
};

export const getUserManual = async () => {
  return api.download(`${documentsUrlPrefix}/user-manual`) as Promise<any>;
};


export const createInstitutionCriterion = async (
  institutionCriterion: InstitutionCriterion
) => {
  return api.post(
    `${institutionsUrlPrefix}/${criteriaUrlPrefix}`,
    institutionCriterion
  ) as Promise<any>;
};

export const updateInstitutionCriterion = async (
  institutionCriterion: InstitutionCriterion
) => {
  return api.put(
    `${institutionsUrlPrefix}/${criteriaUrlPrefix}`,
    institutionCriterion
  ) as Promise<any>;
};

export const deleteInstitutionCriterion = async (
  institutionCriterionId: number
) => {
  return api.delete(
    `${institutionsUrlPrefix}/${criteriaUrlPrefix}/${institutionCriterionId}`
  ) as Promise<any>;
};

export const copyPreviousInstitutionCriterions = async () => {
  return api.post(
    `${institutionsUrlPrefix}/copy-criteria`
  ) as Promise<any>;
};

export const getCriteriaExportFile = async (filters: ExportFiltersData) => {
  return api.download(
    `${criteriaUrlPrefix}/export-list`,
    "get",
    filters
  ) as Promise<any>;
};

export const getPedagogicalYearCriteriaExportFile = async (filters: ExportFiltersData) => {
  return api.download(
    `${criteriaUrlPrefix}/export-pedagogical-year-criteria`,
    "get",
    filters
  ) as Promise<any>;
};

export const searchEnrollmentGroupCriteria = async (
  filters: AdministrationListFiltersData
) => {
  return api.get(
    `${enrollmentGroupUrlPrefix}/${criteriaUrlPrefix}/search`,
    filters
  ) as Promise<InstitutionCriterion[]>;
};

export const getEnrollmentGroupCriterion = async (id: number) => {
  return api.get(
    `${enrollmentGroupUrlPrefix}/${criteriaUrlPrefix}/${id}`
  ) as Promise<InstitutionCriterion>;
};

export const createEnrollmentGroupCriterion = async (
  institutionCriterion: InstitutionCriterion
) => {
  return api.post(
    `${enrollmentGroupUrlPrefix}/${criteriaUrlPrefix}`,
    institutionCriterion
  ) as Promise<any>;
};

export const updateEnrollmentGroupCriterion = async (
  institutionCriterion: InstitutionCriterion
) => {
  return api.put(
    `${enrollmentGroupUrlPrefix}/${criteriaUrlPrefix}`,
    institutionCriterion
  ) as Promise<any>;
};

export const deleteEnrollmentGroupCriterion = async (
  institutionCriterionId: number
) => {
  return api.delete(
    `${enrollmentGroupUrlPrefix}/${criteriaUrlPrefix}/${institutionCriterionId}`
  ) as Promise<any>;
};

export const copyPreviousEnrollmentGroupCriterions = async () => {
  return api.post(
    `${enrollmentGroupUrlPrefix}/copy-criteria`
  ) as Promise<any>;
};

export const getRequestStatuses = async () => {
  return api.get(`${requestStatusesUrlPrefix}`) as Promise<CodeBook[]>;
};

export const getRequestStatusesHistory = async (requestId: number) => {
  return api.get(`${requestStatusesHistoryUrlPrefix}/${requestId}`) as Promise<
    StatusHistoryTableData[]
  >;
};

export const searchInstitutions = async (
  filters: AdministrationListFiltersData
) => {
  return api.get(
    `${enrollmentGroupUrlPrefix}/${institutionsUrlPrefix}/search`,
    filters
  ) as Promise<CodeBook[]>;
};

export const searchLocations = async (
  filters: AdministrationListFiltersData
) => {
  return api.get(
    `${enrollmentGroupUrlPrefix}/${locationsUrlPrefix}/search`,
    filters
  ) as Promise<CodeBook[]>;
};

export const searchRequests = async (
  request: TableStateWithFilters<RequestFilters>
) => {
  return api.get(`${requestsUrlPrefix}/search`, request) as Promise<
    PagedResult<RequestSearchItem>
  >;
};

export const subsequentSearchRequests = async (
  request: TableStateWithFilters<RequestFilters>
) => {
  return api.get(`${requestsUrlPrefix}/subsequent-search`, request) as Promise<
    PagedResult<RequestSearchItem>
  >;
};

export const getRequestPersonInformation = async (id: number) => {
  return api.get(`${personsUrlPrefix}/${id}`) as Promise<RequestPersonInfo>;
};

export const getRequestBasicInfo = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/basic-info`
  ) as Promise<RequestBasicInfo>;
};

export const getLocationProgramsByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/location-programs`
  ) as Promise<LocationProgramListItem[]>;
};

export const getLocations = async () => {
  return api.get(`${institutionsUrlPrefix}/locations`) as Promise<Location[]>;
};

export const getInstitutionsProgramsLocations = async (pedagogicalYearId: number) => {
  return api.get(
    `${institutionsUrlPrefix}/institutions-location-programs?pedagogicalYearId=${pedagogicalYearId}`
  ) as Promise<InstitutionListItem[]>;
};

export const getInstitutionFounderEnrollmentGroup = async () => {
  return api.get(
    `${institutionsUrlPrefix}/institution-founder-enrollment-group`
  ) as Promise<InstitutionFounderEnrollmentGroup>;
};

export const searchLocationProgramData = async (
  filters: TableStateWithFilters<LocationProgramDataListFilters>
) => {
  return api.get(
    `${institutionsUrlPrefix}/locations/data/search`,
    filters
  ) as Promise<PagedResult<LocationProgramDataListItem>>;
};

export const getLocationProgramCapacityExportFile = async (
  filters: LocationProgramExportFilterData
) => {
  return api.download(
    `${institutionsUrlPrefix}/locations/data/export-list`,
    "get",
    filters
  ) as Promise<any>;
};

export const getLocationProgramData = async (id: number) => {
  return api.get(
    `${institutionsUrlPrefix}/locations/data/${id}`
  ) as Promise<LocationProgramData>;
};

export const createLocationProgramData = async (
  locationProgramData: LocationProgramData
) => {
  return api.post(
    `${institutionsUrlPrefix}/locations/data`,
    locationProgramData
  ) as Promise<any>;
};

export const updateLocationProgramData = async (
  locationProgramData: LocationProgramData
) => {
  return api.put(
    `${institutionsUrlPrefix}/locations/data`,
    locationProgramData
  ) as Promise<any>;
};

export const updateLocationProgramDetails = async (
  locationProgramData: LocationProgramData
) => {
  return api.put(
    `${institutionsUrlPrefix}/locations/data/details`,
    locationProgramData
  ) as Promise<any>;
};

export const deleteLocationProgramData = async (
  locationProgramDataId: number
) => {
  return api.delete(
    `${institutionsUrlPrefix}/locations/data/${locationProgramDataId}`
  ) as Promise<any>;
};

export const getRequestCriterionDataByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/criteria-data`
  ) as Promise<RequestCriterionData>;
};

export const evaluateRequest = async (
  criterionData: RequestCriterion[],
  requestId: number
) => {
  return api.post(
    `${requestsUrlPrefix}/${requestId}/evaluate`,
    criterionData
  ) as Promise<any>;
};

export const cancelRequestEvaluation = async (
  requestId: number
) => {
  return api.post(
    `${requestsUrlPrefix}/${requestId}/cancel-evaluation`
  ) as Promise<any>;
};

export const unlockRequest = async (
  unlockRequest: UnlockRequest,
  requestId: number
) => {
  return api.put(
    `${requestsUrlPrefix}/${requestId}/unlock`,
    unlockRequest
  ) as Promise<any>;
};


export const getWorkOrganizations = async () => {
  return api.get(`${workOrganizationsUrlPrefix}`) as Promise<CodeBook[]>;
};

export const getInitialConversationByRequestId = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/initial-conversation`
  ) as Promise<InitialConversationQuestionGroup[]>;
};

export const getInitialConversationExportFile = async (requestId: number) => {
  return api.download(
    `${requestsUrlPrefix}/${requestId}/export-initial-conversation`
  ) as Promise<any>;
};

export const getInitialConversationComments = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/initial-conversation-comments`
  ) as Promise<RequestComment[]>;
};

export const createInitialConversationComment = async (
  requestId: number,
  input: CreateUpdateRequestComment
) => {
  return api.post(
    `${requestsUrlPrefix}/${requestId}/initial-conversation-comment`,
    input
  ) as Promise<any>;
};

export const updateInitialConversationComment = async (
  input: CreateUpdateRequestComment
) => {
  return api.put(
    `${requestsUrlPrefix}/initial-conversation-comment/${input.id}`,
    input
  ) as Promise<any>;
};

export const getRequestListExportFile = async (filters: RequestFilters) => {
  return api.download(
    `${requestsUrlPrefix}/export-list`,
    "get",
    filters
  ) as Promise<any>;
};
export const getSubsequentRequestListExportFile = async (
  filters: RequestFilters
) => {
  return api.download(
    `${requestsUrlPrefix}/subsequent-export-list`,
    "get",
    filters
  ) as Promise<any>;
};

export const getCriterionDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/criterion/${fileId}`
  ) as Promise<any>;
};

export const getApplicantDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/applicant-parent/${fileId}`
  ) as Promise<any>;
};

export const getOtherParentDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/other-parent/${fileId}`
  ) as Promise<any>;
};

export const getChildDocument = async (fileId: string) => {
  return api.download(`${documentsUrlPrefix}/child/${fileId}`) as Promise<any>;
};

export const getInitialConversationDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/initial-conversation/${fileId}`
  ) as Promise<any>;
};

export const getUserSession = async () => {
  return api.get(`${userUrlPrefix}/session`) as Promise<User>;
};

export const getUserAuthData = async () => {
  return api.get(`${userUrlPrefix}/auth-data`) as Promise<UserAuthData[]>;
};

export const deleteRequest = async (requestId: string) => {
  return api.delete(`${requestsUrlPrefix}/${requestId}`) as Promise<any>;
};

export const getPersonCheckData = async (
  applicantFirstName: string,
  applicantLastName: string,
  applicantOib: string,
  childFirstName: string,
  childLastName: string,
  childOib: string
) => {
  return api.get(`${personsUrlPrefix}/check-data`, {
    applicantFirstName: applicantFirstName,
    applicantLastName: applicantLastName,
    applicantOib: applicantOib,
    childFirstName: childFirstName,
    childLastName: childLastName,
    childOib: childOib,
  }) as Promise<CheckDataResult>;
};

export const getAuthorizationForm = async (request: string) => {
  return api.get(`${authorizationPrefix}/initialize`, {
    requestId: encodeURIComponent(request),
  }) as Promise<RequestAuthorization>;
};

export const cancelAuthorizationForm = async (request: string) => {
  return api.get(`${authorizationPrefix}/cancel`, {
    requestId: encodeURIComponent(request),
  }) as Promise<CancelAuthorization>;
};

export const grantAuthorizationForm = async (
  authorization: RequestAuthorization
) => {
  return api.post(
    `${authorizationPrefix}/grant`,
    authorization
  ) as Promise<SubmitAuthorization>;
};

export const canCreateRequest = async (
  childOib: string,
  requestTypeId: number
) => {
  return api.get(`${requestsUrlPrefix}/can-create`, {
    childOib: childOib,
    requestTypeId: requestTypeId,
  }) as Promise<CanCreateRequestResult>;
};

export const searchEnrollmentList = async (
  request: TableStateWithFilters<EnrollmentFilters>
) => {
  return api.get(`${enrollmentListsUrlPrefix}/search`, request) as Promise<
    PagedResult<EnrollmentSearchItem>
  >;
};

export const exportEnrollmentList = async (
  request: EnrollmentFilters
) => {
  return api.download(
    `${enrollmentListsUrlPrefix}/export-list`,
    "get",
    request
  ) as Promise<any>;
};

export const getEnrollmentStatuses = async () => {
  return api.get(`${enrollmentStatusesUrlPrefix}`) as Promise<CodeBook[]>;
};

export const getEnrollmentRejectReasons = async () => {
  return api.get(enrollmentRejectReasonUrlPrefix) as Promise<CodeBook[]>;
};

export const rejectEnrollmentRequests = async (
  enrollment: ConfirmRejectEnrollment
) => {
  return api.post(
    `${enrollmentListsUrlPrefix}/reject`,
    enrollment
  ) as Promise<any>;
};

export const confirmEnrollmentRequests = async (
  enrollment: ConfirmRejectEnrollment
) => {
  return api.post(
    `${enrollmentListsUrlPrefix}/confirm`,
    enrollment
  ) as Promise<any>;
};

export const cancelConfirmedEnrollmentRequests = async (
  enrollmentRequests: Enrollment[]
) => {
  return api.post(
    `${enrollmentListsUrlPrefix}/cancel`,
    enrollmentRequests
  ) as Promise<any>;
};

export const cancelRejectedEnrollmentRequests = async (
  enrollmentRequestIds: number[]
) => {
  return api.post(
    `${enrollmentListsUrlPrefix}/cancel-rejected`,
    enrollmentRequestIds
  ) as Promise<any>;
};

export const getPhysicalExaminationData = async (requestId: string) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/physical-examination`
  ) as Promise<RequestPhysicalExamination>;
};

export const getPhysicalExaminationDocument = async (fileId: string) => {
  return api.download(
    `${documentsUrlPrefix}/physical-examination/${fileId}`
  ) as Promise<any>;
};

export const rejectPhysicalExamination = async (
  unlockReason: UnlockReason,
  requestId: number
) => {
  return api.put(
    `${requestsUrlPrefix}/${requestId}/reject-physical-examination`,
    unlockReason
  ) as Promise<any>;
};

export const confirmPhysicalExamination = async (requestId: number) => {
  return api.put(
    `${requestsUrlPrefix}/${requestId}/confirm-physical-examination`
  ) as Promise<any>;
};

export const searchEnrollmentGroupBenfits = async (
  filters: AdministrationListFiltersData
) => {
  return api.get(
    `${enrollmentGroupUrlPrefix}/${benefitsUrlPrefix}/search`,
    filters
  ) as Promise<InstitutionBenefit[]>;
};

export const searchInstitutionBenefits = async (
  filters: AdministrationListFiltersData
) => {
  return api.get(
    `${institutionsUrlPrefix}/${benefitsUrlPrefix}/search`,
    filters
  ) as Promise<InstitutionBenefit[]>;
};

export const searchBeneficiary = async (
  request: TableStateWithFilters<BeneficiaryFilter>
) => {
  return api.get(
    `${beneficiaryUrlPrefix}/search`,
    request
  ) as Promise<PagedResult<Beneficiary>>;
};

export const exportBeneficiaryList = async (
  request: BeneficiaryFilter
) => {
  return api.download(
    `${beneficiaryUrlPrefix}/export-list`,
    "get",
    request
  ) as Promise<any>;
};


export const deleteInstitutionBenefit = async (
  institutionBenefitId: number
) => {
  return api.delete(
    `${institutionsUrlPrefix}/${benefitsUrlPrefix}/${institutionBenefitId}`
  ) as Promise<any>;
};

export const deleteEnrollmentGroupBenefit = async (
  institutionBenefitId: number
) => {
  return api.delete(
    `${enrollmentGroupUrlPrefix}/${benefitsUrlPrefix}/${institutionBenefitId}`
  ) as Promise<any>;
};

export const createEnrollmentGroupBenefit = async (
  institutionBenefit: InstitutionBenefit
) => {
  return api.post(
    `${enrollmentGroupUrlPrefix}/${benefitsUrlPrefix}`,
    institutionBenefit
  ) as Promise<any>;
};

export const createInstitutionBenefit = async (
  institutionBenefit: InstitutionBenefit
) => {
  return api.post(
    `${institutionsUrlPrefix}/${benefitsUrlPrefix}`,
    institutionBenefit
  ) as Promise<any>;
};

export const getEnrollmentGroupBenefit = async (id: number) => {
  return api.get(
    `${enrollmentGroupUrlPrefix}/${benefitsUrlPrefix}/${id}`
  ) as Promise<InstitutionBenefit>;
};

export const getInstitutionBenefit = async (id: number) => {
  return api.get(
    `${institutionsUrlPrefix}/${benefitsUrlPrefix}/${id}`
  ) as Promise<InstitutionBenefit>;
};

export const getBenefits = async () => {
  return api.get(`${benefitsUrlPrefix}`) as Promise<Benefit[]>;
};

export const getPedagogicalYearExportFile = async (exportTypeId: number) => {
  return api.download(
    `${pedagogicalYearsUrlPrefix}/${exportTypeId}/export-list`
  ) as Promise<any>;
};

export const getRegularImportantDates = async (pedagogicalYearId: number) => {
  return api.get(
    `${importantDatesUrlPrefix}/pedagogical-year/${pedagogicalYearId}`
  ) as Promise<PedagogicalYearImportantDate>;
};

export const getImportantDateDetails = async (importantDateId: number) => {
  return api.get(
    `${importantDatesUrlPrefix}/${importantDateId}`
  ) as Promise<ImportantDate>;
};

export const updateRegularImportantDates = async (
  regularImportantDates: ImportantDate[]
) => {
  return api.put(
    `${importantDatesUrlPrefix}/important-dates`,
    regularImportantDates
  ) as Promise<any>;
};

export const getSubsequentImportantDates = async (
  params: AdministrationListFiltersData
) => {
  return api.get(
    `${importantDatesUrlPrefix}/subsequent-important-dates`,
    params
  ) as Promise<MonthImportantDate>;
};

export const searchImportantDateMonths = async (
  filters: AdministrationListFiltersData
) => {
  return api.get(`${importantDatesUrlPrefix}/months`, filters) as Promise<
    CodeBook[]
  >;
};

export const getMonthsExportFile = async (filters: ExportFiltersData) => {
  return api.download(
    `${importantDatesUrlPrefix}/months-export-list`,
    "get",
    filters
  ) as Promise<any>;
};

export const getImportantDatePedagogicalYears = async () => {
  return api.get(`${pedagogicalYearsUrlPrefix}/important-date`) as Promise<
    PedagogicalYear[]
  >;
};

export const getPdfDocumentByRequestId = async (requestId: number) => {
  return api.download(
    `${requestsUrlPrefix}/${requestId}/download-pdf`
  ) as Promise<any>;
};

export const getZipDocumentByRequestId = async (requestId: number) => {
  return api.download(
    `${requestsUrlPrefix}/${requestId}/download-zip`
  ) as Promise<any>;
};

export const getEnrollmentGroupBenefitListExportFile = async (
  filters: ExportFiltersData
) => {
  return api.download(
    `${enrollmentGroupUrlPrefix}/benefits/export-list`,
    "get",
    filters
  ) as Promise<any>;
};

export const getInstitutionBenefitListExportFile = async (
  filters: ExportFiltersData
) => {
  return api.download(
    `${institutionsUrlPrefix}/benefits/export-list`,
    "get",
    filters
  ) as Promise<any>;
};

export const getInstitutionDetails = async () => {
  return api.get(
    `${institutionsUrlPrefix}/details`
  ) as Promise<InstitutionDetailsData>;
};

export const sendMailForErrorData = async (
  errorUserData: ErrorUserData
) => {
  return api.post(`${mailUrlPrefix}/send`,
    errorUserData) as Promise<ErrorUserData>;
};

export const sendPhysicalExaminationDocumentation = async (
  formData: FormData
) => {
  return api.post(
    `${requestsUrlPrefix}/physical-examination`,
    formData
  ) as Promise<any>;
};

export const getPhysicalExamination = async (requestId: number) => {
  return api.get(
    `${requestsUrlPrefix}/${requestId}/physical-examination`
  ) as Promise<PhysicalExaminationData>;
};

export const isPedagogicalYearInstitutionActive = async () => {
  return api.get(
    `${institutionsUrlPrefix}/isPedagogicalYearInstitutionActive`
  ) as Promise<boolean>;
};

export const sendEmails = async (
  requestId: number
) => {
  return api.post(
    `${requestsUrlPrefix}/${requestId}/sendEmails`,
  ) as Promise<any>;
};

export const getExchangeRateEurToHrk = async () => {
  return api.get(
    `${exchangeRateUrlPrefix}/eurToHrk`
  ) as Promise<number>;
};

export const login = async () => {
  return api.get(
    `${userUrlPrefix}/login`
  ) as Promise<void>
}